/**
 * =========================================================
 * OrgSwitcher — Kalues Organizatash për Header
 * =========================================================
 * Qëllimi: Dropdown për pronarët që menaxhojnë shumë klinika.
 *          Shfaqet vetëm kur businessRole === "owner".
 *          Klik → navigo te /app/businesses/{id}.
 * Endpoint: GET /api/v1/organizations/mine
 * =========================================================
 */

import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Building2, ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/api/client";
import { useAuthStore } from "@/lib/auth/authStore";

interface OrgOut {
  id: number; name: string; slug: string | null;
  owner_account_id: number; created_at: string;
}

const fetchOrgs = async (): Promise<OrgOut[]> =>
  (await apiClient.get<OrgOut[]>("/organizations/mine")).data;

export const OrgSwitcher = (): JSX.Element | null => {
  const { businessRole } = useAuthStore();
  const [open, setOpen]  = useState(false);
  const ref              = useRef<HTMLDivElement>(null);
  const navigate         = useNavigate();

  const searchParams = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
  const activeOrgId  = searchParams.get("org") ? Number(searchParams.get("org")) : null;

  const { data: orgs = [] } = useQuery({
    queryKey:  ["organizations", "mine"],
    queryFn:   fetchOrgs,
    enabled:   businessRole === "owner",
    staleTime: 120_000,
  });

  const activeOrg = activeOrgId
    ? orgs.find((o) => o.id === activeOrgId) ?? orgs[0]
    : orgs[0];

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  if (businessRole !== "owner" || orgs.length === 0) return null;

  return (
    <div ref={ref} className="relative hidden lg:block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 rounded-[12px] border border-border bg-secondary/50 px-3 py-1.5 text-xs font-semibold text-foreground transition hover:bg-secondary"
      >
        <Building2 className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
        <span className="max-w-[120px] truncate">{activeOrg?.name ?? "Organizata"}</span>
        <ChevronDown className={`h-3 w-3 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>

      {open && (
        <div className="elite-surface absolute left-0 top-10 z-50 min-w-[200px] rounded-[18px] border p-2">
          <p className="mb-1 px-2 text-[9px] font-bold uppercase tracking-widest text-muted-foreground">
            Klinkat e mia
          </p>
          {orgs.map((org) => (
            <button
              key={org.id}
              type="button"
              onClick={() => {
                // CEO Dashboard — përditëson të gjithë dashboard-in per këtë org
                navigate(`/app/corporate?org=${org.id}`);
                setOpen(false);
              }}
              className="flex w-full items-center gap-2 rounded-[12px] px-2 py-2 text-left text-xs font-medium text-foreground transition hover:bg-secondary/60"
            >
              <Building2 className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
              <span className="truncate">{org.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * =========================================================
 * Fund i OrgSwitcher
 * =========================================================
 */
